import { create } from 'zustand';

import { activeCapabilities } from '../input/activeAdapter';
import { isUnlocked } from './audioUnlock';

/**
 * The one control offered after an utterance did not reach the person's ears (`SUR-23`).
 *
 * ── The problem it started as (3.2) ──
 *
 * A dwell completion or a scan tick is not a Safari user gesture, so `speechSynthesis.speak()` is
 * *silently dropped*. Phase 1 built the seam that keeps that from being a mystery: `inGesture()` returns
 * false for a timed adapter and `WebSpeech.isAvailable` honours it, so the chain skips the platform voice
 * rather than handing it an utterance it will discard. Correct, and not sufficient — the chain then
 * reaches `visualOnly`, the sentence appears full-screen, and the person asked to be *heard*.
 *
 * ── Why it now has three levels instead of one, and why they are levels (C80, ADR-042) ──
 *
 * It was gated to timed modes, on the argument that in direct touch a button which could only hand the
 * same utterance to `speechSynthesis` again would look like a fix without being one. **That argument was
 * right, and its premise has changed.** The button can now repair the audio path, which is the thing that
 * would actually help — so the gate comes off, and direct touch is the mode this person uses.
 *
 * The three are levels inside one control rather than three controls, or two band tenants, because every
 * one of them is raised by the same event: a speak attempt that failed. Two tenants raised by one event
 * fire together on essentially every failure, and then the precedence between them is a coin toss — which
 * is the exact problem `bandTenant.ts` exists to prevent. A level is not a precedence question, so
 * `bandTenant()`'s five steps did not change and `GEO-09` stayed literally true.
 */

/**
 * `retry` — say it again; the tap is the gesture the platform voice needed.
 * `repair` — put the audio path back together first (`SPK-19`), then say it again.
 * `reload` — the last resort, and never offered first (`SHL-09`).
 */
export type RepairLevel = 'retry' | 'repair' | 'reload';

interface TapToSpeakState {
  /** The utterance waiting for a real gesture, or null. Never rendered — only spoken. */
  pending: string | null;
  level: RepairLevel;
  offer: (text: string, level: RepairLevel) => void;
  /** After a repair that did not work. The only route to `reload`. */
  escalate: () => void;
  clear: () => void;
}

export const useTapToSpeak = create<TapToSpeakState>((set) => ({
  pending: null,
  level: 'retry',
  offer: (text, level) => set({ pending: text, level }),
  // The text is kept: the reload has a sentence to preserve, and the person may yet tap to speak it.
  escalate: () => set({ level: 'reload' }),
  clear: () => set({ pending: null, level: 'retry' }),
}));

/** Providers that do not play through our `AudioContext`, and so cannot report it broken by failing. */
const BYPASSES_OUR_CONTEXT = new Set(['webSpeech', 'visualOnly']);

/**
 * Should the control be offered for this utterance, and at which level?
 *
 * Called by `SpeechService` the moment it knows which provider served the utterance. Deliberately given
 * the resolved provider rather than reading the service back, so the decision is a pure function of what
 * just happened.
 */
export function offerTapToSpeak(text: string, providerId: string): void {
  const timed = activeCapabilities().isTimed;

  if (providerId === 'visualOnly') {
    // Nothing was said. In a timed mode a tap alone may fix it, because a timer was the whole problem;
    // in direct touch the audio path itself is suspect and a retry would change nothing.
    useTapToSpeak.getState().offer(text, timed ? 'retry' : 'repair');
    return;
  }

  /**
   * The quiet case, and the one C71 left behind: the utterance **succeeded**, in the wrong voice.
   *
   * A suspended or dead context makes every provider that plays through it report itself unavailable, so
   * the chain routes around the damage to `speechSynthesis` — a different voice, no warning, and the next
   * utterance will do the same. The person was heard, so nothing looks wrong, and nothing offers to fix
   * the thing that is.
   */
  if (BYPASSES_OUR_CONTEXT.has(providerId) && !isUnlocked()) {
    useTapToSpeak.getState().offer(text, 'repair');
  }
}
